/**
 * What a sale produced and never moved: registration fees and resale settlements,
 * from the gallery's side and the artist's.
 */
import type {
  BillingContactResponse,
  SetBillingContactRequest,
  RecordFeePaidRequest,
  RegistrationFeesResponse,
  GallerySettlementsResponse,
  ArtistSettlementsResponse,
  AnswerSettlementRequest,
  SettleResaleRequest,
} from "@ondo/shared";

import { request, requestWith } from "./request.ts";

/**
 * The registration fees the firm owes — live, recorded as paid, and void.
 * Read through `principal_ids_at_least('viewer')`; recording is a director's.
 */
export const fetchRegistrationFees = () =>
  request<RegistrationFeesResponse>("/gallery/fees").then((body) => body.fees);

/** The firm's word that it paid. Admin, append-once; ONDO takes nothing. */
export const recordFeePaid = (feeId: string, body: RecordFeePaidRequest) =>
  request<void>(`/gallery/fees/${feeId}/paid`, body);

/**
 * What this gallery's covenants have produced: the Resale settlements screen.
 *
 * A THIRD read of gallery sales, and a different relation again. The inventory
 * is live consignments; Sold Works is what this firm placed; this is what a
 * STRANGER's sale owes it. `transfers_read` admits the caller to the second and
 * not the third, so `ondo.gallery_settlements()` is a definer whose whole job is
 * that disclosure — decision 3 of 20260906090000_resale_settlements.sql.
 */
export const fetchGallerySettlements = () =>
  request<GallerySettlementsResponse>("/gallery/settlements").then(
    (body) => body.settlements,
  );

/**
 * Recording a collection or a remittance, and stating the rate if one is wanted.
 *
 * IT MOVES NO MONEY. It records that a director SAYS money moved, dated and
 * attributed — and the artist's leg is an assertion the artist may answer.
 */
export const settleResale = (id: string, body: SettleResaleRequest) =>
  request<void>(`/settlements/${id}/settle`, body satisfies SettleResaleRequest);

/**
 * The ARTIST's side of the same rows, and narrower.
 *
 * `mine` rather than a route taking an ONDO id, matching `/consignments/mine`:
 * an ONDO id is attached, never discovered. It answers empty for everybody who
 * is not named on a settlement, which is almost everybody.
 */
export const fetchArtistSettlements = () =>
  request<ArtistSettlementsResponse>("/settlements/mine").then(
    (body) => body.settlements,
  );

/**
 * Confirming or disputing a remittance.
 *
 * The only control an artist has over a figure somebody else chose — including
 * the exchange rate that sized it. It writes BESIDE the gallery's row and never
 * edits it.
 */
export const answerSettlement = (id: string, body: AnswerSettlementRequest) =>
  request<void>(`/settlements/${id}/answer`, body satisfies AnswerSettlementRequest);

/**
 * Who the firm's ONDO fee notices, statements and invoices go to — OND-93.
 *
 * `principal` IS REQUIRED, unlike every other route that takes one: a billing
 * contact belongs to exactly one firm, so there is nothing for an absent one to
 * mean. Read at any active seat; changing it is a director's.
 */
export const fetchBillingContact = (principal: string) =>
  request<BillingContactResponse>(
    `/gallery/billing-contact?principal=${encodeURIComponent(principal)}`,
  );

/**
 * Names somebody else, or clears the choice.
 *
 * `contactOndoId: null` CLEARS rather than naming nobody: the default — the
 * firm's longest-standing active admin seat — is always underneath, so clearing
 * is how a firm goes back to following it. The effective contact comes back
 * either way, so the screen never has to work out what clearing meant.
 */
export const setBillingContact = (principal: string, contactOndoId: string | null) =>
  requestWith<BillingContactResponse>("PUT", "/gallery/billing-contact", {
    principal,
    contactOndoId,
  } satisfies SetBillingContactRequest);
